import { useEffect, useState } from 'react';
import { supervisorApi } from '../../api/supervisor.js';
import { adminApi } from '../../api/admin.js';

const CONDITION_TYPES = [
  { value: 'user_explicit', label: 'Specific user' },
  { value: 'department',    label: 'Department' },
  { value: 'role',          label: 'Role' },
  { value: 'tag',           label: 'User tag' },
];

const RESOLUTION_TYPES = [
  { value: 'explicit_user',     label: 'Specific user' },
  { value: 'department_head',   label: 'Department head' },
  { value: 'role_senior',       label: 'Senior in role' },
  { value: 'parent_dept_head',  label: 'Parent dept head' },
];

export default function SupervisorRules() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  // Pickers data
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tags, setTags] = useState([]);
  const [departments, setDepartments] = useState([]);

  function refresh() { supervisorApi.listRules().then((d) => setItems(d?.items || [])); }
  useEffect(refresh, []);
  useEffect(() => {
    adminApi.users({ limit: 200 }).then((d) => setUsers(d?.items || []));
    adminApi.roles().then((d) => setRoles(d?.items || []));
    supervisorApi.listTags().then((d) => setTags(d?.items || []));
    fetch((import.meta.env.VITE_API_BASE_URL || '/api') + '/departments', {
      headers: { Authorization: `Bearer ${localStorage.getItem('bos.access')}` },
    }).then((r) => r.json()).then((j) => setDepartments(j?.data?.items || []));
  }, []);

  async function move(id, dir) {
    const idx = items.findIndex((r) => r.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    const updates = next.map((r, i) => ({ id: r.id, priority: next.length - i - 1 }));
    setItems(next);
    try { await supervisorApi.reorderRules(updates); }
    catch (err) { setError(err.response?.data?.message || 'Reorder failed'); refresh(); }
  }

  async function save(rule) {
    setError(null); setInfo(null);
    try {
      if (rule.id) await supervisorApi.updateRule(rule.id, rule);
      else await supervisorApi.createRule(rule);
      setEditing(null);
      refresh();
      setInfo('Saved');
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    }
  }

  async function remove(id) {
    if (!window.confirm('Delete this rule?')) return;
    try { await supervisorApi.deleteRule(id); refresh(); }
    catch (err) { setError(err.response?.data?.message || 'Delete failed'); }
  }

  async function preview() {
    const userId = window.prompt('User UUID to preview resolution for?');
    if (!userId) return;
    try {
      const r = await supervisorApi.previewResolution(userId);
      const chainNames = r.chain.map((c) => c.name).join(' → ');
      alert(`Resolved supervisor: ${r.supervisor?.name || 'none'}\nChain: ${chainNames || '—'}`);
    } catch (err) {
      alert(err.response?.data?.message || 'Preview failed');
    }
  }

  return (
    <section>
      <header className="page-header">
        <h1>Supervisor Rules</h1>
        <div className="actions">
          <button className="btn ghost" onClick={preview}>Preview resolution</button>
          <button className="btn primary" onClick={() => setEditing(emptyRule())}>+ New Rule</button>
        </div>
      </header>
      {error && <div className="alert error">{error}</div>}
      {info && <div className="alert info">{info}</div>}

      <p className="muted small">Rules evaluate top-down (highest priority first). The first rule whose condition matches wins. If no rule matches, the user's explicit <code>supervisor_id</code> is used; final fallback is org admin.</p>

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Order</th><th>Name</th><th>If</th><th>Then</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map((r, i) => {
              const cond = parseJson(r.condition_value);
              const ref = parseJson(r.supervisor_ref);
              return (
                <tr key={r.id}>
                  <td>
                    <button className="btn ghost small" onClick={() => move(r.id, -1)} disabled={i === 0}>↑</button>
                    <button className="btn ghost small" onClick={() => move(r.id, +1)} disabled={i === items.length - 1}>↓</button>
                  </td>
                  <td><strong>{r.rule_name}</strong></td>
                  <td className="muted small">{r.condition_type}: {summary(cond)}</td>
                  <td className="muted small">{r.supervisor_resolution} {summary(ref)}</td>
                  <td><span className={`chip status-${r.is_active ? 'active' : 'cancelled'}`}>{r.is_active ? 'active' : 'paused'}</span></td>
                  <td>
                    <button className="btn ghost small" onClick={() => setEditing({ ...r, condition_value: cond, supervisor_ref: ref, escalation_chain: parseJson(r.escalation_chain) })}>Edit</button>
                    <button className="btn ghost small" onClick={() => remove(r.id)}>Delete</button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="muted">No rules yet — the explicit supervisor_id is used as fallback.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <RuleEditor
          rule={editing}
          users={users} roles={roles} tags={tags} departments={departments}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function emptyRule() {
  return {
    rule_name: 'New rule',
    priority: 0,
    condition_type: 'department',
    condition_value: { any: true },
    supervisor_resolution: 'department_head',
    supervisor_ref: {},
    escalation_chain: [],
    max_escalation_levels: 3,
    is_active: true,
  };
}

function summary(obj) {
  if (!obj) return '';
  return Object.entries(obj).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v).slice(0, 24)}`).join(', ');
}
function parseJson(v) { if (v == null) return null; if (typeof v === 'object') return v; try { return JSON.parse(v); } catch { return null; } }

function RuleEditor({ rule, users, roles, tags, departments, onSave, onCancel }) {
  const [r, setR] = useState(rule);
  function p(patch) { setR({ ...r, ...patch }); }
  function pCondVal(v) { p({ condition_value: { ...r.condition_value, ...v } }); }
  function pRef(v) { p({ supervisor_ref: { ...r.supervisor_ref, ...v } }); }

  return (
    <div className="card form-card">
      <header className="page-header">
        <h3>{r.id ? 'Edit rule' : 'New rule'}</h3>
        <button className="btn ghost small" onClick={onCancel}>✕</button>
      </header>
      <div className="grid-form">
        <label className="full">Rule name<input value={r.rule_name} onChange={(e) => p({ rule_name: e.target.value })} /></label>
        <label>Priority<input type="number" value={r.priority} onChange={(e) => p({ priority: Number(e.target.value) })} /></label>
        <label>Active<select value={r.is_active ? '1' : '0'} onChange={(e) => p({ is_active: e.target.value === '1' })}><option value="1">Yes</option><option value="0">No</option></select></label>

        <label>If condition type
          <select value={r.condition_type} onChange={(e) => p({ condition_type: e.target.value, condition_value: {} })}>
            {CONDITION_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>

        {r.condition_type === 'user_explicit' && (
          <label>User
            <select value={r.condition_value.user_id || ''} onChange={(e) => pCondVal({ user_id: e.target.value })}>
              <option value="">Select…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
            </select>
          </label>
        )}
        {r.condition_type === 'department' && (
          <label>Department (or any)
            <select value={r.condition_value.department_id || (r.condition_value.any ? 'ANY' : '')} onChange={(e) => {
              if (e.target.value === 'ANY') pCondVal({ any: true, department_id: undefined });
              else pCondVal({ department_id: e.target.value, any: undefined });
            }}>
              <option value="">Select…</option>
              <option value="ANY">Any department</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
        )}
        {r.condition_type === 'role' && (
          <label>Role
            <select value={r.condition_value.role || ''} onChange={(e) => pCondVal({ role: e.target.value })}>
              <option value="">Select…</option>
              {roles.map((rl) => <option key={rl.id} value={rl.name}>{rl.name}</option>)}
            </select>
          </label>
        )}
        {r.condition_type === 'tag' && (
          <label>Tag (slug)
            <select value={r.condition_value.tag || ''} onChange={(e) => pCondVal({ tag: e.target.value })}>
              <option value="">Select…</option>
              {tags.map((t) => <option key={t.id} value={t.slug}>{t.name}</option>)}
            </select>
          </label>
        )}

        <label>Then resolve via
          <select value={r.supervisor_resolution} onChange={(e) => p({ supervisor_resolution: e.target.value, supervisor_ref: {} })}>
            {RESOLUTION_TYPES.map((rt) => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
          </select>
        </label>

        {r.supervisor_resolution === 'explicit_user' && (
          <label>Supervisor user
            <select value={r.supervisor_ref.user_id || ''} onChange={(e) => pRef({ user_id: e.target.value })}>
              <option value="">Select…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
            </select>
          </label>
        )}
        {r.supervisor_resolution === 'role_senior' && (
          <label>Role
            <select value={r.supervisor_ref.role || ''} onChange={(e) => pRef({ role: e.target.value })}>
              <option value="">Select…</option>
              {roles.map((rl) => <option key={rl.id} value={rl.name}>{rl.name}</option>)}
            </select>
          </label>
        )}

        <label>Max escalation levels<input type="number" min={1} max={5} value={r.max_escalation_levels} onChange={(e) => p({ max_escalation_levels: Number(e.target.value) })} /></label>
      </div>

      <div className="actions">
        <button className="btn ghost" onClick={onCancel}>Cancel</button>
        <button className="btn primary" onClick={() => onSave({
          ruleName: r.rule_name,
          priority: r.priority,
          conditionType: r.condition_type,
          conditionValue: r.condition_value,
          supervisorResolution: r.supervisor_resolution,
          supervisorRef: r.supervisor_ref,
          escalationChain: r.escalation_chain || [],
          maxEscalationLevels: r.max_escalation_levels,
          isActive: r.is_active,
          id: r.id,
        })}>Save</button>
      </div>
    </div>
  );
}
